<template>
  <div class="locationaddress">
    <v-layout row wrap>
      <v-flex xs12>
        <FormSectionTitle>Inspection Address</FormSectionTitle>

        <SelectInputCommon
          v-model="value.country"
          :items="countries"
          :validations="{ required: true }"
          display-name="Country"
          ref="country" />
      </v-flex>

      <v-flex xs12>
        <TextInputCommon
          v-model="value.address1"
          :validations="{ ...InputValidations.address1, required: true }"
          display-name="Street Address"
          identifier="address"
          ref="address1"
          @keypress.enter="$event.preventDefault()" />
      </v-flex>

      <v-flex xs12>
        <TextInputCommon
          v-model="value.address2"
          :validations="InputValidations.address2"
          display-name="Street Address 2 (optional)"
          identifier="unit"
          ref="address2" />
      </v-flex>

      <v-flex xs12 sm6 lg6>
        <TextInputCommon
          v-model="value.city"
          :validations="{ ...InputValidations.city, required: true }"
          display-name="City"
          ref="city" />
      </v-flex>

      <v-flex xs12 sm6 lg3>
        <TextInputCommon
          v-model="value.state"
          :validations="{ ...InputValidations.state, required: true }"
          :display-name="localization.state"
          identifier="state"
          maxlength="3"
          outline
          ref="state" />
      </v-flex>

      <v-flex xs12 lg3>
        <TextInputCommon
          v-model="value.postalCode"
          :validations="{ ...InputValidations.postalCode, required: true }"
          :display-name="localization.postCode"
          identifier="zip"
          ref="postalCode" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { Constants as SharedConstants, Utilities } from '@homegauge/shared';
import { Constants } from '@homegauge/vue';

import FormSectionTitle from '@/components/FormSectionTitle.vue';
import SelectInputCommon from '@/components/common/SelectInputCommon.vue';
import TextInputCommon from '@/components/common/TextInputCommon.vue';

const { InputValidations } = Constants;
const { Countries } = SharedConstants;
const { LocalizationUtility } = Utilities;

const DEFAULT_MODEL = {
  address1: '',
  address2: '',
  city: '',
  postalCode: '',
  state: '',
  country: Countries.unitedStates.name,
};

export default {
  components: {
    FormSectionTitle,
    SelectInputCommon,
    TextInputCommon,
  },
  inject: ['$validator'],
  props: {
    value: {
      type: Object,
      default: () => DEFAULT_MODEL,
    },
  },
  data() {
    return {
      countries: Object.values(Countries).map(x => ({ text: x.name, value: x.abbreviation })),
      InputValidations,
    };
  },
  mounted() {
    const autocompleteEl = document.getElementById('input_address');
    if (autocompleteEl) {
      this.autocomplete = new window.google.maps.places.Autocomplete(autocompleteEl, { types: ['geocode'] });
      this.autocomplete.setFields(['address_component']);
      this.autocomplete.addListener('place_changed', this.fillInAddress);
      autocompleteEl.placeholder = '';
    }
  },
  computed: {
    localization() {
      return LocalizationUtility
        .getLocalizationByCountryCode(this.value.country)
        .address;
    },
  },
  methods: {
    fillInAddress() {
      const place = this.autocomplete.getPlace();
      // early return if there are no address_components. this can be
      // triggered by highlighting an incomplete address and then
      // attempting to submit the form with enter key
      if (!place.address_components) {
        return;
      }

      this.value.address1 = '';
      this.value.address2 = '';

      place.address_components.forEach(addressComponent => {
        const addressType = addressComponent.types[0];
        const text = addressComponent.short_name;

        switch (addressType) {
          case 'street_number':
          case 'route':
            this.value.address1 += `${text} `;
            break;
          case 'premise':
          case 'subpremise':
            this.value.address2 += `${text} `;
            break;
          case 'locality':
            this.value.city = text;
            break;
          case 'administrative_area_level_1':
            this.value.state = text;
            break;
          case 'postal_code':
            this.value.postalCode = text;
            break;
          case 'country':
            this.value.country = text;
            break;
          default: break;
        }
      });

      this.value.address1 = this.value.address1.trim();
      this.value.address2 = this.value.address2.trim();
    },
  },
};
</script>
